import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Clock, CalendarX, Building2, Target, Circle, Pencil, EyeOff, Eye, Sparkles, LayoutDashboard, ChevronRight, PenLine } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useAssistant } from '../assistant/AssistantContext';
import { useToday, useCompleteTask, useReopenTask, usePatchTask, usePatchOpportunity, useTodayPreferences, useTodaySettings, useDashboardStats, errorMessage } from '../api/queries';
import RescheduleChips from '../components/RescheduleChips';
import ActionItemEditModal from '../components/ActionItemEditModal';
import InlineField from '../components/InlineField';
import { fmtCurrency } from '../utils/formatCurrency';
import { today as localToday } from '../capture/dates';
import './Today.css';

// The home page: what to do now. Every row acts in place. The rules are
// SQL on the server (server/today); the one AI action, "Draft a check-in",
// hands the note panel a seed and lets the person approve what comes back.

const SECTIONS = [
  { key: 'overdue', title: 'Overdue', icon: AlertCircle, tone: 'danger', kind: 'task' },
  { key: 'due_today', title: 'Due today', icon: Clock, tone: 'warning', kind: 'task' },
  { key: 'quiet_accounts', title: 'Quiet accounts', icon: Building2, tone: 'ai', kind: 'account' },
  { key: 'stale_opportunities', title: 'Past close date', icon: Target, tone: 'warning', kind: 'opportunity' },
  { key: 'undated', title: 'No due date', icon: CalendarX, tone: 'muted', kind: 'task' },
];


const dayDiff = (iso, todayStr) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const a = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const [y, m, dd] = todayStr.split('-').map(Number);
  const b = new Date(y, m - 1, dd);
  return Math.round((b - a) / 86400000);
};

const lateLabel = (task, todayStr) => {
  const diff = dayDiff(task.due_date, todayStr);
  if (diff === null) return 'no due date';
  if (diff > 0) return `${diff} day${diff === 1 ? '' : 's'} late`;
  if (diff === 0) {
    const t = new Date(task.due_date);
    const hasTime = /T\d{2}:\d{2}/.test(String(task.due_date)) && !/T00:00/.test(String(task.due_date));
    return hasTime ? `due ${t.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : 'due today';
  }
  return `due in ${-diff} day${diff === -1 ? '' : 's'}`;
};

const shortDate = (iso) => {
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

export default function Today() {
  const { user, canDo, hasPermission } = useAuth();
  const toast = useToast();
  const { openWith } = useAssistant();
  const todayStr = localToday();
  const { data, isPending, error } = useToday(todayStr);
  const stats = useDashboardStats();
  const complete = useCompleteTask();
  const reopen = useReopenTask();
  const patchTask = usePatchTask();
  const patchOpp = usePatchOpportunity();
  const prefs = useTodayPreferences();
  const settings = useTodaySettings();
  const [editing, setEditing] = useState(null);
  const [showMuted, setShowMuted] = useState({});
  const [pickingDate, setPickingDate] = useState(null);

  const muted = (data && data.muted) || [];
  const canEditTasks = canDo('tasks', 'edit');
  const canEditOpps = canDo('opportunities', 'edit');
  const canNote = canDo('activities', 'create');
  const isManager = hasPermission('manage_settings');

  const setMuted = (next) => prefs.mutate(next, { onError: (err) => toast.error(errorMessage(err, 'Could not save that')) });
  const mute = (key) => setMuted([...muted.filter((k) => k !== key), key]);
  const unmute = (key) => setMuted(muted.filter((k) => k !== key));

  const completeTask = async (task) => {
    try {
      await complete.mutateAsync(task.id);
      toast.undo(`“${task.title}” marked complete`, () => reopen.mutate(task.id));
    } catch (err) { toast.error(errorMessage(err, 'Could not complete that')); }
  };
  const reschedule = async (task, due_date) => {
    try {
      await patchTask.mutateAsync({ id: task.id, patch: { due_date } });
      toast.success(`“${task.title}” moved to ${shortDate(due_date)}`);
    } catch (err) { toast.error(errorMessage(err, 'Could not move that')); }
  };
  const redateOpp = async (opp, day) => {
    setPickingDate(null);
    try {
      await patchOpp.mutateAsync({ id: opp.id, patch: { expected_close_date: day } });
      toast.success(`${opp.name} now expected ${shortDate(day)}`);
    } catch (err) { toast.error(errorMessage(err, 'Could not change the date')); }
  };
  const markLost = async (opp) => {
    if (!data || !data.lost_stage_id) return;
    const previous = opp.stage_id;
    try {
      await patchOpp.mutateAsync({ id: opp.id, patch: { stage_id: data.lost_stage_id } });
      toast.undo(`${opp.name} marked lost`, () => patchOpp.mutate({ id: opp.id, patch: { stage_id: previous } }));
    } catch (err) { toast.error(errorMessage(err, 'Could not mark it lost')); }
  };

  // The one AI action: the facts the rules know, handed to the note as the
  // person's own words, for them to approve what comes back.
  const draftCheckIn = (account) => {
    const since = account.last_activity_at ? `since ${shortDate(account.last_activity_at)} (${account.days_quiet} days)` : 'on record at all';
    const facts = [
      `Re-engage ${account.name}: no contact ${since}.`,
      account.open_opportunities > 0 ? `${account.open_opportunities} open deal${account.open_opportunities === 1 ? '' : 's'} worth $${fmtCurrency(account.pipeline_value)}.` : null,
      account.open_tasks > 0 ? `${account.open_tasks} open action item${account.open_tasks === 1 ? '' : 's'} already on the account.` : null,
      'Draft a short check-in note and one next step to get back in touch this week.',
    ].filter(Boolean).join(' ');
    openWith(`About ${account.name} (account id ${account.id}): ${facts}`);
  };

  const dateLabel = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });

  if (isPending && !data) {
    return <div className="today-page"><div className="loading-container"><div className="spinner"></div></div></div>;
  }
  if (error && !data) {
    return <div className="today-page"><div className="alert alert-error" role="alert">Could not load your day.</div></div>;
  }

  const sections = SECTIONS.filter((s) => (s.kind === 'task' ? canDo('tasks', 'view') : s.kind === 'account' ? canDo('accounts', 'view') : canDo('opportunities', 'view')));
  const visible = sections.filter((s) => !muted.includes(s.key));
  const mutedSections = sections.filter((s) => muted.includes(s.key));
  const nothingToDo = visible.every((s) => data.counts[s.key] === 0);

  const renderTask = (task) => (
    <li key={task.id} className="today-row">
      {canEditTasks ? (
        <button type="button" className="today-check" onClick={() => completeTask(task)} aria-label={`Mark “${task.title}” complete`} title="Mark complete"><Circle size={18} /></button>
      ) : <span className="today-check" aria-hidden="true"><Circle size={18} /></span>}
      <div className="today-main">
        <div className="today-title">{task.title}</div>
        <div className="today-meta">
          {task.account_id && <Link to={`/accounts/${task.account_id}`}>{task.account_name}</Link>}
          {task.contact_name && <span>· {task.contact_name}</span>}
          {task.opportunity_name && <span>· <Link to={`/opportunities/${task.opportunity_id}`}>{task.opportunity_name}</Link></span>}
          <span className={`today-due ${dayDiff(task.due_date, todayStr) > 0 ? 'is-late' : ''}`}>· {lateLabel(task, todayStr)}</span>
        </div>
      </div>
      {canEditTasks && (
        <div className="today-actions">
          <RescheduleChips onPick={(due) => reschedule(task, due)} disabled={patchTask.isPending} />
          <button type="button" className="btn btn-icon btn-sm" onClick={() => setEditing(task)} aria-label={`Edit “${task.title}”`} title="Edit"><Pencil size={14} /></button>
        </div>
      )}
    </li>
  );

  const renderAccount = (account) => (
    <li key={account.id} className="today-row">
      <span className="today-check today-check-static" aria-hidden="true"><Building2 size={16} /></span>
      <div className="today-main">
        <div className="today-title"><Link to={`/accounts/${account.id}`}>{account.name}</Link>{account.is_key_account ? <span className="badge badge-primary today-badge">Key account</span> : null}</div>
        <div className="today-meta">
          <span>{account.last_activity_at ? `last touch ${account.days_quiet} days ago` : 'never contacted'}</span>
          {account.open_opportunities > 0 && <span>· {account.open_opportunities} open deal{account.open_opportunities === 1 ? '' : 's'} · ${fmtCurrency(account.pipeline_value)}</span>}
          {account.open_tasks > 0 && <span>· {account.open_tasks} open item{account.open_tasks === 1 ? '' : 's'}</span>}
        </div>
      </div>
      {canNote && (
        <div className="today-actions">
          <button type="button" className="chip chip-ai" onClick={() => draftCheckIn(account)}><Sparkles size={13} aria-hidden="true" /> Draft a check-in</button>
        </div>
      )}
    </li>
  );

  const renderOpportunity = (opp) => (
    <li key={opp.id} className="today-row">
      <span className="today-check today-check-static" aria-hidden="true"><Target size={16} /></span>
      <div className="today-main">
        <div className="today-title"><Link to={`/opportunities/${opp.id}`}>{opp.name}</Link></div>
        <div className="today-meta">
          {opp.account_id && <Link to={`/accounts/${opp.account_id}`}>{opp.account_name}</Link>}
          {opp.stage_name && <span>· {opp.stage_name}</span>}
          {opp.amount > 0 && <span>· ${fmtCurrency(opp.amount)}</span>}
          <span className="today-due is-late">· was due {shortDate(opp.expected_close_date)}, {opp.days_past} day{opp.days_past === 1 ? '' : 's'} ago</span>
        </div>
      </div>
      {canEditOpps && (
        <div className="today-actions">
          {pickingDate === opp.id ? (
            <input type="date" className="chip-date" aria-label={`New close date for ${opp.name}`} autoFocus onChange={(e) => { if (e.target.value) redateOpp(opp, e.target.value); }} onBlur={() => setPickingDate(null)} />
          ) : (
            <button type="button" className="chip" onClick={() => setPickingDate(opp.id)}>New date</button>
          )}
          {data.lost_stage_id && <button type="button" className="chip" onClick={() => markLost(opp)}>Mark lost</button>}
        </div>
      )}
    </li>
  );

  return (
    <div className="today-page">
      <div className="page-header today-header">
        <div>
          <h1 className="page-title">Today</h1>
          <p className="today-date">{dateLabel}{user ? ` · ${user.first_name || user.name}` : ''}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-primary" onClick={() => openWith(null, { hint: 'note' })} title="Tell the AI what happened; it proposes the entries for you to approve">
            <PenLine size={16} aria-hidden="true" />
            Log a note
          </button>
        </div>
      </div>

      <nav className="today-counts" aria-label="Summary">
        {visible.map((s) => (
          <a key={s.key} href={`#today-${s.key}`} className={`today-count tone-${s.tone} ${data.counts[s.key] === 0 ? 'is-zero' : ''}`}>
            <s.icon size={14} aria-hidden="true" />
            <strong>{data.counts[s.key]}</strong> {s.title.toLowerCase()}
          </a>
        ))}
      </nav>

      {nothingToDo && (
        <div className="card today-clear"><div className="card-body">
          <h3>Nothing waiting on you.</h3>
          <p className="text-secondary">No overdue items, nothing due today, no quiet accounts, no deals past their date. Open the AI Assistant to log what happened, or open the <Link to="/dashboard">dashboard</Link>.</p>
        </div></div>
      )}

      {visible.map((s) => {
        const rows = data[s.key] || [];
        if (rows.length === 0) return null;
        const more = data.counts[s.key] - rows.length;
        return (
          <section key={s.key} id={`today-${s.key}`} className={`card today-section tone-${s.tone}`} aria-labelledby={`today-${s.key}-title`}>
            <div className="card-header today-section-header">
              <h3 className="card-title" id={`today-${s.key}-title`}><s.icon size={16} aria-hidden="true" /> {s.title} <span className="today-section-count">{data.counts[s.key]}</span></h3>
              <div className="today-section-tools">
                {s.key === 'quiet_accounts' && (
                  <span className="today-threshold">
                    quiet after{' '}
                    {isManager ? (
                      <InlineField
                        value={data.quiet_days}
                        type="number"
                        label="quiet-account threshold in days"
                        onSave={(n) => settings.mutateAsync(n)}
                        format={(v) => `${v} days`}
                      />
                    ) : `${data.quiet_days} days`}
                  </span>
                )}
                <button type="button" className="btn btn-icon btn-sm" onClick={() => mute(s.key)} aria-label={`Mute ${s.title}`} title="Hide this section"><EyeOff size={14} /></button>
              </div>
            </div>
            <ul className="today-list">
              {rows.map((row) => (s.kind === 'task' ? renderTask(row) : s.kind === 'account' ? renderAccount(row) : renderOpportunity(row)))}
            </ul>
            {more > 0 && (
              <div className="today-more">
                {s.kind === 'task' ? <Link to={`/action-items?status=Open${s.key === 'overdue' ? '&overdue=true' : ''}`}>and {more} more <ChevronRight size={14} /></Link>
                  : s.kind === 'account' ? <Link to="/accounts">and {more} more <ChevronRight size={14} /></Link>
                  : <Link to="/opportunities">and {more} more <ChevronRight size={14} /></Link>}
              </div>
            )}
          </section>
        );
      })}

      {mutedSections.length > 0 && (
        <div className="today-muted">
          {mutedSections.map((s) => (
            <span key={s.key} className="today-muted-item">
              <s.icon size={13} aria-hidden="true" /> {s.title} ({data.counts[s.key]})
              {showMuted[s.key] ? (
                <>
                  <button type="button" className="btn-link" onClick={() => unmute(s.key)}><Eye size={12} /> Unmute</button>
                </>
              ) : (
                <button type="button" className="btn-link" onClick={() => setShowMuted({ ...showMuted, [s.key]: true })}>Show</button>
              )}
            </span>
          ))}
        </div>
      )}
      {mutedSections.filter((s) => showMuted[s.key]).map((s) => {
        const rows = data[s.key] || [];
        return (
          <section key={`m-${s.key}`} className={`card today-section tone-${s.tone}`} aria-label={`${s.title} (muted)`}>
            <div className="card-header today-section-header">
              <h3 className="card-title"><s.icon size={16} aria-hidden="true" /> {s.title} <span className="today-section-count">{data.counts[s.key]}</span></h3>
              <button type="button" className="btn btn-icon btn-sm" onClick={() => unmute(s.key)} aria-label={`Unmute ${s.title}`} title="Show this section every day"><Eye size={14} /></button>
            </div>
            {rows.length === 0 ? <p className="card-body text-secondary">Nothing here.</p> : (
              <ul className="today-list">{rows.map((row) => (s.kind === 'task' ? renderTask(row) : s.kind === 'account' ? renderAccount(row) : renderOpportunity(row)))}</ul>
            )}
          </section>
        );
      })}

      <div className="today-strip">
        <Link to="/dashboard" className="today-strip-link"><LayoutDashboard size={14} aria-hidden="true" /> Dashboard</Link>
        {stats.data && (
          <>
            {canDo('opportunities', 'view') && <span className="today-stat"><strong>{stats.data.opportunities?.open ?? 0}</strong> open deals</span>}
            {canDo('opportunities', 'view') && <span className="today-stat"><strong>${fmtCurrency(stats.data.opportunities?.pipelineValue)}</strong> pipeline</span>}
            {canDo('tasks', 'view') && <span className="today-stat"><strong>{stats.data.tasks?.open ?? 0}</strong> open items</span>}
            {canDo('accounts', 'view') && <span className="today-stat"><strong>{stats.data.accounts?.total ?? 0}</strong> accounts</span>}
          </>
        )}
      </div>

      <ActionItemEditModal task={editing} isOpen={!!editing} onClose={() => setEditing(null)} />
    </div>
  );
}
